import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import TimeAgo from "react-timeago";
import CounterOfferModal from "./Components/ui/modals/CounterOfferModal";
import { createCounterOffer, approveCounterOffer, rejectCounterOffer } from "./services/userService";
import { showToast } from "./utils/Toast";
    
const calculateRemainingTime = (createdAt) => {
  if (!createdAt) return null;
  
  const created = new Date(createdAt);
  const expiry = new Date(created.getTime() + 24 * 60 * 60 * 1000); // +24 hours
  const now = new Date();
  const remaining = expiry - now;
  
  if (remaining <= 0) return null; // Expired
  
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
  
  return { hours, minutes, seconds, totalMs: remaining };
};


// Updated MyLocalCarsCard component with modal
const MyLocalCarsCard = ({ localCar, needsRefresh, setNeedsRefresh }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [counterOfferAmount, setCounterOfferAmount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [remainingTime, setRemainingTime] = useState(null);
  const dropdownRef = useRef(null);

  const navigate = useNavigate();

  // Update remaining time every second
  useEffect(() => {
    if (localCar?.counterOffer?.createdAt) {
      const updateTimer = () => {
        const time = calculateRemainingTime(localCar.counterOffer.createdAt);
        setRemainingTime(time);
      };
      
      updateTimer(); // Initial update
      const interval = setInterval(updateTimer, 1000);
      
      return () => clearInterval(interval);
    } else {
      setRemainingTime(null);
    }
  }, [localCar?.counterOffer?.createdAt]);

  // Check if counter offer has expired
  const isCounterOfferExpired = () => {
    if (!localCar?.counterOffer?.createdAt) return false;
    const time = calculateRemainingTime(localCar.counterOffer.createdAt);
    return time === null; 
  };

  const handleNavigate = () => {
    navigate(`/local-vehicle-detail/${localCar?.id || "-"}`);
  };

  const handleActionSelect = async (action) => {
    if (action === "Countered") {
      setIsModalOpen(true);
      setIsDropdownOpen(false);
    } else if (action === "Accepted") {
      await handleAcceptCounterOffer();
    } else if (action === "Rejected") {
      await handleRejectCounterOffer();
    } else {
      setSelectedAction(action);
      setIsDropdownOpen(false);
      console.log(`Action selected for car ${localCar?.id}: ${action}`);
    }
  };

  const handleAcceptCounterOffer = async () => {
    try {
      if (!localCar?.counterOffer?.id) {
        showToast("No counter offer found to accept", "error");
        return;
      }

      const response = await approveCounterOffer(localCar.counterOffer.id);
      
      
      setSelectedAction("Accepted");
      setIsDropdownOpen(false);
      showToast("Counter offer accepted successfully!", "success");
      
      // Refresh the data
      if (setNeedsRefresh) {
        setNeedsRefresh(true);
      }
      
      console.log("Counter offer accepted:", response);
    } catch (error) {
      console.error("Error accepting counter offer:", error);
      
      if (error.response?.data?.message) {
        showToast(`Error: ${error.response.data.message}`, "error");
      } else {
        showToast("Failed to accept counter offer. Please try again.", "error");
      }
    }
  };

  const handleRejectCounterOffer = async () => {
    try {
      if (!localCar?.counterOffer?.id) {
        showToast("No counter offer found to reject", "error");
        return;
      }
      const response = await rejectCounterOffer(localCar.counterOffer.id);
      
      setSelectedAction("Rejected");
      setIsDropdownOpen(false);
      showToast("Counter offer rejected successfully!", "success");
      // Refresh the data
      if (setNeedsRefresh) {
        setNeedsRefresh(true);
      }
      console.log("Counter offer rejected:", response);
    } catch (error) {
      console.error("Error rejecting counter offer:", error);
      
      if (error.response?.data?.message) {
        showToast(`Error: ${error.response.data.message}`, "error");
      } else {
        showToast("Failed to reject counter offer. Please try again.", "error");
      }
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setCounterOfferAmount("");
  };

  const handleCounterOfferSubmit = async () => {
    if (counterOfferAmount && !isNaN(Number(counterOfferAmount))) {
      setIsSubmitting(true);
      try {
        const minAllowed = Number(localCar?.counterOffer?.counterOfferPrice) || 0;
        const numeric = Number(counterOfferAmount);
        if (numeric < minAllowed) {
          showToast(`Counter offer must be at least ${minAllowed.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`, "error");
          setIsSubmitting(false);
          return;
        }
        const counterOfferData = {
          carID: localCar?.id,
          counterOfferPrice: Number(counterOfferAmount),
          sender: "seller", // User owns the car, so they are the seller
          carTitle: localCar?.title,
        };
        
        await createCounterOffer(counterOfferData);
        setNeedsRefresh(true);
        setSelectedAction("Countered");
        setIsModalOpen(false);
        console.log(`Counter offer submitted for car ${localCar?.id}: $${counterOfferAmount}`);
        setCounterOfferAmount("");
        showToast("Counter offer submitted successfully!", "success");
        // redirect seller after countering
        try {
          const nav = require('react-router-dom').useNavigate?.();
          if (nav) nav('/user/account/local-cars');
        } catch {}
        
      } catch (error) {
        console.error("Error submitting counter offer:", error);
        
        if (error.response?.data?.message === "Not your turn") {
          showToast("It's not your turn to make a counter offer. Please wait for the other party to respond.", "warning");
        } else if (error.response?.data?.message) {
          showToast(`Error: ${error.response.data.message}`, "error");
        } else {
          showToast("Failed to submit counter offer. Please try again.", "error");
        }
      } finally {
        // setNeedsRefresh(true);
        setIsSubmitting(false);
      }
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  console.log("======", localCar );

  return (
    <>
      <tr className="border-t">
        {/* Car Image */}
        <td className="px-4 lg:px-[1vw] py-2 lg:py-[0.5vw] lg:text-[1vw] font-medium text-gray-700 text-nowrap">
          <div className="w-[60px] lg:w-[3.125vw] h-[60px] lg:h-[3.125vw] rounded-md lg:rounded-[0.375vw] overflow-hidden bg-gray-100">
            <img
              className="w-full h-full object-cover"
              src={localCar?.carImages[0] || ""}
              alt="Car"
            />
          </div>
        </td>
        {/* Car Title */}
        <td
          className="px-4 lg:px-[1vw] py-2 lg:py-[0.5vw] lg:text-[1vw] font-medium text-gray-700 text-nowrap hover:text-blue-800 hover:underline cursor-pointer"
          onClick={handleNavigate}
        >
          {`${localCar?.year || "-"} ${localCar?.make || "-"} ${
            localCar?.model || "-"
          }`}
        </td>
        <td className="px-4 lg:px-[1vw] py-2 lg:py-[0.5vw] lg:text-[1vw] font-medium text-gray-700 text-nowrap">{localCar?.status || "-"}</td>
        {/* Highest Bid */}
        <td className="px-4 lg:px-[1vw] py-2 lg:py-[0.5vw] lg:text-[1vw] font-medium text-gray-700 text-nowrap">
          {(() => {
            const numeric = Number(localCar?.currentBid);
            return !isNaN(numeric) && localCar?.currentBid !== undefined && localCar?.currentBid !== null && localCar?.currentBid !== ""
              ? numeric.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
              : 'N/A';
          })()}
        </td>
        {/* Counter Offer */}
        <td className="px-4 lg:px-[1vw] py-2 lg:py-[0.5vw] lg:text-[1vw] font-medium text-gray-700 text-nowrap">
          <div className="flex items-center gap-2">
            <div>
              {(() => {
                const numeric = Number(localCar?.counterOffer?.counterOfferPrice);
                return !isNaN(numeric) && localCar?.counterOffer?.counterOfferPrice !== undefined && localCar?.counterOffer?.counterOfferPrice !== null && localCar?.counterOffer?.counterOfferPrice !== ""
                  ? numeric.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                  : 'N/A';
              })()}
            </div>
            {remainingTime && (
              <div className="text-[10px] lg:text-[0.7vw] text-red-600 font-semibold flex items-center gap-1">
                <span>⏰</span>
                <span>{remainingTime.hours}h {remainingTime.minutes}m {remainingTime.seconds}s</span>
              </div>
            )}
          </div>
        </td>
        
        {/* Posted Time Ago */}
        <td className="px-4 lg:px-[1vw] py-2 lg:py-[0.5vw] lg:text-[1vw] font-medium text-gray-700 text-nowrap">
          <TimeAgo date={localCar?.createdAt} />
        </td>
        {/* Action - Only show if status is "Sold On Approval" */}
        <td className="px-4 lg:px-[1vw] py-2 lg:py-[0.5vw] lg:text-[1vw] font-medium text-gray-700 text-nowrap relative">
          {localCar?.status === "Sold On Approval" && !isCounterOfferExpired() ? (
            <div className="relative" ref={dropdownRef}>
              {/* Check if there's a counter offer and who made it */}
              {localCar?.counterOffer && localCar?.counterOffer?.sender === 'seller' ? (
                // If seller made counter offer, show "Countered" status
                <div className="px-3 py-1 text-sm bg-blue-500 text-white rounded-md text-center w-full">
                  Countered
                </div>
              ) : (
                // Navigate to vehicle detail to review/act on offer
                <button
                  onClick={() => {
                    const bidPrice = typeof localCar?.currentBid === 'number' ? localCar.currentBid : null;
                    const offerPrice = localCar?.counterOffer?.counterOfferPrice ?? null;
                    const counterOfferId = localCar?.counterOffer?.id ?? null;
                    navigate(`/local-vehicle-detail/${localCar?.id || '-'}` , {
                      state: {
                        bidPrice,
                        offerPrice,
                        counterOfferId,
                      }
                    });
                  }}
                  className="px-3 py-1 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                >
                  Review Offer
                </button>
              )}
            </div>
          ) : localCar?.status === "Sold On Approval" && isCounterOfferExpired() && localCar?.counterOffer ? (
            <div className="px-3 py-1 text-sm bg-red-500 text-white rounded-md text-center w-full">
              Timeout
            </div>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </td>
      </tr>
      
      <CounterOfferModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleCounterOfferSubmit}
        amount={counterOfferAmount}
        setAmount={setCounterOfferAmount}
        carTitle={`${localCar?.year || ""} ${localCar?.make || ""} ${localCar?.model || ""}`}
        minAmount={Number(localCar?.counterOffer?.counterOfferPrice) || 0}
        isLoading={isSubmitting}
      />
    </>
  );
};


export default MyLocalCarsCard;
